import { useMemo, useEffect } from 'react';
import { Link } from 'react-router';
import { useAuthStore } from '../../stores/authStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { usePortfoliosStore } from '../../stores/portfoliosStore';
import { useWalletEquityStore } from '../../stores/walletEquityStore';
import type { Portfolio } from '../../api/portfoliosApi';
import { formatUsd } from '../../utils/tradeMetrics';

/**
 * Read-only header pill that shows the active wallet's total value.
 *
 * Priority: live (teal border) > paper (muted border) > nothing.
 *
 * Live total: polled every 30 s from walletEquityStore. While the very first
 * fetch is in flight we show a skeleton shimmer rather than an empty/£0 pill.
 * If the live fetch errors (e.g. dangling API key from issue-011) we fall back
 * silently to the paper pill, or render nothing if no paper portfolio exists.
 *
 * Routing:
 *   Both pills → /trading (the Portfolios overview — the user's wallets and how
 *   each is doing). Previously the two pills diverged (live → /wallet?tab=live,
 *   paper → /strategy); they now land on the same overview for consistency.
 */

const fmtUsd = (v: number) => formatUsd(v);

// ── Wallet icon (inline SVG — no icon lib dependency) ──────────────────────
function WalletIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className ?? 'w-3.5 h-3.5'}
      aria-hidden="true"
    >
      {/* Heroicons 24/solid — wallet (bifold with coin pocket) */}
      <path d="M2.273 5.625A4.483 4.483 0 0 1 6.75 3h10.5c1.785 0 3.333.947 4.177 2.364A4.49 4.49 0 0 0 18.75 5H5.25a.75.75 0 0 0 0 1.5h13.5a3 3 0 0 1 3 3v.75A4.493 4.493 0 0 0 21.75 9H5.25A1.5 1.5 0 0 1 3.75 7.5V6.9c0-.46.055-.907.16-1.337a4.49 4.49 0 0 1-1.637.062Z" />
      <path d="M3.75 8.25A.75.75 0 0 0 3 9v8.25A3.75 3.75 0 0 0 6.75 21h10.5A3.75 3.75 0 0 0 21 17.25V9.75A2.25 2.25 0 0 0 18.75 7.5H3.75ZM15 13.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z" />
    </svg>
  );
}

// ── Skeleton shimmer ────────────────────────────────────────────────────────
function Shimmer() {
  return (
    <div className="h-4 w-16 rounded animate-pulse bg-accent/20" aria-hidden="true" />
  );
}

// ── Individual pill variants ────────────────────────────────────────────────
function LivePill({ total, loading }: { total: number | null; loading: boolean }) {
  return (
    <Link
      to="/trading"
      title="View your portfolios"
      aria-label="Live wallet total — click to view your portfolios"
      className="flex items-center gap-1.5 rounded border border-accent bg-accent/5 px-2.5 py-1 text-xs transition-colors hover:bg-accent/10 max-w-[160px]"
    >
      <WalletIcon className="w-3.5 h-3.5 text-accent shrink-0" />
      <span className="text-accent font-semibold tracking-wide uppercase shrink-0">
        LIVE
      </span>
      <span className="text-text-primary font-medium truncate">
        {loading && total === null ? <Shimmer /> : total !== null ? fmtUsd(total) : '—'}
      </span>
    </Link>
  );
}

function PaperPill({ portfolio }: { portfolio: Portfolio }) {
  // Prefer live snapshot equity when available; fall back to currentBalance.
  const snapshot = usePortfoliosStore(
    (s) => s.snapshotsByPortfolio[portfolio._id] ?? null,
  );
  const total = snapshot?.totalEquity ?? portfolio.currentBalance;

  return (
    <Link
      to="/trading"
      title="View your portfolios"
      aria-label="Paper portfolio total — click to view your portfolios"
      className="flex items-center gap-1.5 rounded border border-border bg-bg-secondary px-2.5 py-1 text-xs transition-colors hover:border-accent/40 max-w-[160px]"
    >
      <WalletIcon className="w-3.5 h-3.5 text-text-muted shrink-0" />
      <span className="text-text-muted font-semibold tracking-wide uppercase shrink-0">
        PAPER
      </span>
      <span className="text-text-primary font-medium truncate">{fmtUsd(total)}</span>
    </Link>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export function HeaderWalletPill() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const selectedMarket = useSettingsStore((s) => s.selectedMarket);

  // ── Paper portfolio (in-memory) ──
  // Select the whole array for stable reference; derive active paper list
  // with useMemo to avoid "Maximum update depth exceeded" (documented issue).
  const allPortfolios = usePortfoliosStore((s) => s.portfolios);
  const activePaperPortfolios = useMemo(
    () => allPortfolios.filter((p) => p.type === 'paper' && p.isActive),
    [allPortfolios],
  );
  const selectedPaperId = usePortfoliosStore((s) => s.selectedPaperPortfolioId);
  const activePaper: Portfolio | null = useMemo(
    () =>
      activePaperPortfolios.find((p) => p._id === selectedPaperId) ??
      activePaperPortfolios[0] ??
      null,
    [activePaperPortfolios, selectedPaperId],
  );

  // ── Live balance (polled store) ──
  const startPolling = useWalletEquityStore((s) => s.startPolling);
  const stopPolling = useWalletEquityStore((s) => s.stopPolling);
  const liveTotal = useWalletEquityStore((s) => s.totalUsd);
  const liveLoading = useWalletEquityStore((s) => s.loading);
  const liveError = useWalletEquityStore((s) => s.error);
  const hasActiveLiveWallet = useWalletEquityStore((s) => s.activeLiveWallet !== null);

  // Start/stop the poll timer with authentication lifecycle.
  useEffect(() => {
    if (!isAuthenticated) {
      stopPolling();
      return;
    }
    void startPolling();
    return () => {
      // Don't stop on unmount — the store lives for the session lifetime.
      // stopPolling() is called explicitly on logout via authStore.
    };
  }, [isAuthenticated, startPolling, stopPolling]);

  // Not signed in → render nothing.
  if (!isAuthenticated) return null;

  // ── Decide which pill to show ──
  // Live pill: if a live wallet exists AND no error OR loading in progress.
  const liveAvailable = hasActiveLiveWallet && (liveLoading || liveError === null);

  if (liveAvailable) {
    return <LivePill total={liveTotal} loading={liveLoading} />;
  }

  // Paper pill: spot-only (paper portfolios are spot-market-scoped today).
  // Live pill may still appear on non-spot if live wallet exists.
  if (activePaper && selectedMarket === 'spot') {
    return <PaperPill portfolio={activePaper} />;
  }

  // Nothing to show.
  return null;
}
